import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Award, Zap, RefreshCw, PlusCircle, MinusCircle, Search, CheckCircle2, Settings } from 'lucide-react';

export default function AdminOkPro() {
    const [boosts, setBoosts] = useState([]);
    const [stats, setStats] = useState(null);
    const [pricing, setPricing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('active');
    const [adjustOpen, setAdjustOpen] = useState(false);
    const [pricingOpen, setPricingOpen] = useState(false);
    const [adjustForm, setAdjustForm] = useState({ nickname: '', delta: '', note: '' });
    const [pricingForm, setPricingForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: bts }, { data: st }, { data: pr }] = await Promise.all([
                api.get('/admin/okpro/boosts', { params: filterStatus !== '__all__' ? { status: filterStatus } : {} }),
                api.get('/admin/okpro/stats'),
                api.get('/admin/okpro/pricing'),
            ]);
            setBoosts(Array.isArray(bts) ? bts : []);
            setStats(st);
            setPricing(pr);
        } catch (e) { toast.error('Error al cargar'); }
        finally { setLoading(false); }
    }, [filterStatus]);

    useEffect(() => { load(); }, [load]);

    const openPricing = () => {
        setPricingForm(JSON.parse(JSON.stringify(pricing)));
        setPricingOpen(true);
    };

    const savePricing = async () => {
        setBusy(true);
        try {
            await api.put('/admin/okpro/pricing', pricingForm);
            toast.success('Precios actualizados');
            setPricingOpen(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setBusy(false); }
    };

    const doAdjust = async (delta) => {
        if (!adjustForm.nickname) { toast.error('Escribe el nickname del vendedor'); return; }
        setBusy(true);
        try {
            await api.put('/admin/okpro/adjust-credits', { nickname: adjustForm.nickname, delta: Number(delta), note: adjustForm.note });
            toast.success(`${delta > 0 ? '+' : ''}${delta} créditos ajustados para @${adjustForm.nickname}`);
            setAdjustOpen(false);
            setAdjustForm({ nickname: '', delta: '', note: '' });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setBusy(false); }
    };

    const filtered = boosts.filter(b => !search || b.user_nickname?.toLowerCase().includes(search.toLowerCase()) || b.ad_title?.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="space-y-5" data-testid="admin-okpro">
            <div className="flex items-center gap-3 flex-wrap">
                <Award className="h-6 w-6 text-amber-500" />
                <h1 className="text-2xl font-semibold">OkPro · Gestión de Super Boost</h1>
                <Button size="sm" variant="outline" onClick={load} className="ml-auto"><RefreshCw className="h-4 w-4 mr-1" />Actualizar</Button>
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                        { label: 'Boosts activos', value: stats.active_boosts ?? 0, color: 'text-amber-600' },
                        { label: 'Créditos usados hoy', value: stats.credits_used_today ?? 0, color: 'text-rose-600' },
                        { label: 'Vendedores con boost', value: stats.sellers_with_boost ?? 0, color: 'text-blue-600' },
                        { label: 'Total histórico', value: stats.total_boosts ?? 0, color: 'text-emerald-600' },
                    ].map(s => (
                        <div key={s.label} className="rounded-xl border bg-card p-4">
                            <div className="text-xs uppercase text-muted-foreground tracking-wider">{s.label}</div>
                            <div className={`text-3xl font-bold mt-1 ${s.color}`}>{s.value}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pricing summary */}
            {pricing && (
                <div className="rounded-xl border bg-card p-4 space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="font-medium flex items-center gap-2"><Settings className="h-4 w-4 text-muted-foreground" />Configuración de precios</div>
                        <Button size="sm" variant="outline" onClick={openPricing}>Editar precios</Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {(pricing.durations || []).map(d => (
                            <div key={d.days} className="text-sm rounded-lg border p-2.5 bg-secondary/30">
                                <div className="font-medium">{d.days} días</div>
                                <div className="text-muted-foreground text-xs">{d.credits} crédito{d.credits !== 1 ? 's' : ''}</div>
                            </div>
                        ))}
                    </div>
                    <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide mt-1">Paquetes de créditos</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {(pricing.packages || []).map((p, i) => (
                            <div key={i} className="text-sm rounded-lg border p-2.5 bg-secondary/30">
                                <div className="font-medium">{p.credits} créditos</div>
                                <div className="text-muted-foreground text-xs">${(p.price_mxn || 0).toLocaleString('es-MX')} MXN</div>
                                <div className="text-muted-foreground text-xs">{p.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 flex-wrap items-center">
                <Button onClick={() => setAdjustOpen(true)} data-testid="adjust-credits-btn">
                    <PlusCircle className="h-4 w-4 mr-2" />Ajustar créditos de usuario
                </Button>
                <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input className="pl-8" placeholder="Buscar por vendedor o anuncio…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
            </div>

            {/* Filter tabs */}
            <Tabs value={filterStatus} onValueChange={setFilterStatus}>
                <TabsList>
                    <TabsTrigger value="active">Activos</TabsTrigger>
                    <TabsTrigger value="expired">Expirados</TabsTrigger>
                    <TabsTrigger value="__all__">Todos</TabsTrigger>
                </TabsList>
                <TabsContent value={filterStatus} className="pt-4">
                    {loading ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">Cargando…</div>
                    ) : filtered.length === 0 ? (
                        <div className="rounded-2xl border border-dashed py-14 text-center text-sm text-muted-foreground">No hay boosts con este filtro.</div>
                    ) : (
                        <div className="space-y-3">
                            {filtered.map(b => (
                                <div key={b.id} className="rounded-xl border bg-card p-4 flex items-center gap-4" data-testid={`admin-boost-${b.id}`}>
                                    <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                                        <Zap className="h-5 w-5 text-amber-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-medium truncate">{b.ad_title || b.ad_id}</span>
                                            {b.status === 'active'
                                                ? <Badge className="bg-amber-100 text-amber-800"><CheckCircle2 className="h-3 w-3 mr-1" />Activo</Badge>
                                                : <Badge variant="outline">{b.status}</Badge>}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                            @{b.user_nickname} · {b.duration_days} días · {b.credits_used} créd.
                                        </div>
                                        {b.ends_at && <div className="text-[10px] text-muted-foreground">Vence: {new Date(b.ends_at).toLocaleDateString('es-MX')}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Adjust credits dialog */}
            <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Ajustar créditos OkPro</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Nickname del vendedor</Label>
                            <Input value={adjustForm.nickname} onChange={(e) => setAdjustForm(f => ({ ...f, nickname: e.target.value.replace('@', '') }))} placeholder="carlos_mtz" data-testid="adjust-nickname" />
                        </div>
                        <div>
                            <Label>Créditos a ajustar</Label>
                            <Input type="number" value={adjustForm.delta} onChange={(e) => setAdjustForm(f => ({ ...f, delta: e.target.value }))} placeholder="Positivo = agregar, negativo = quitar" data-testid="adjust-delta" />
                        </div>
                        <div>
                            <Label>Nota interna</Label>
                            <Input value={adjustForm.note} onChange={(e) => setAdjustForm(f => ({ ...f, note: e.target.value }))} placeholder="Ej: Compensación por error de sistema" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setAdjustOpen(false)}>Cancelar</Button>
                        <Button variant="outline" className="border-red-200 text-red-700" onClick={() => doAdjust(-Math.abs(Number(adjustForm.delta)))} disabled={busy || !adjustForm.nickname || !adjustForm.delta}>
                            <MinusCircle className="h-4 w-4 mr-1" />Quitar
                        </Button>
                        <Button onClick={() => doAdjust(Math.abs(Number(adjustForm.delta)))} disabled={busy || !adjustForm.nickname || !adjustForm.delta}>
                            <PlusCircle className="h-4 w-4 mr-1" />Agregar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Pricing config dialog */}
            {pricingForm && (
                <Dialog open={pricingOpen} onOpenChange={setPricingOpen}>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>Configurar precios OkPro</DialogTitle></DialogHeader>
                        <div className="space-y-5">
                            {/* Boost durations cost */}
                            <div>
                                <div className="font-medium text-sm mb-2">Costo en créditos por duración de boost</div>
                                <div className="space-y-2">
                                    {(pricingForm.durations || []).map((d, i) => (
                                        <div key={d.days} className="flex items-center gap-3">
                                            <span className="text-sm w-20 shrink-0">{d.days} días</span>
                                            <Input
                                                type="number" min="1" step="1"
                                                value={d.credits}
                                                onChange={e => {
                                                    const durations = [...pricingForm.durations];
                                                    durations[i] = { ...d, credits: Number(e.target.value) || 1 };
                                                    setPricingForm(f => ({ ...f, durations }));
                                                }}
                                                className="w-24"
                                            />
                                            <span className="text-xs text-muted-foreground">crédito{d.credits !== 1 ? 's' : ''}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Credit packages */}
                            <div>
                                <div className="font-medium text-sm mb-2">Paquetes de créditos (precio en MXN)</div>
                                <div className="space-y-2">
                                    {(pricingForm.packages || []).map((p, i) => (
                                        <div key={i} className="grid grid-cols-3 gap-2 items-center">
                                            <div>
                                                <Label className="text-xs">Etiqueta</Label>
                                                <Input value={p.label} onChange={e => {
                                                    const packages = [...pricingForm.packages];
                                                    packages[i] = { ...p, label: e.target.value };
                                                    setPricingForm(f => ({ ...f, packages }));
                                                }} placeholder="Starter" className="h-8" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Créditos</Label>
                                                <Input type="number" min="1" value={p.credits} onChange={e => {
                                                    const packages = [...pricingForm.packages];
                                                    packages[i] = { ...p, credits: Number(e.target.value) };
                                                    setPricingForm(f => ({ ...f, packages }));
                                                }} className="h-8" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Precio MXN</Label>
                                                <Input type="number" min="0" value={p.price_mxn} onChange={e => {
                                                    const packages = [...pricingForm.packages];
                                                    packages[i] = { ...p, price_mxn: Number(e.target.value) };
                                                    setPricingForm(f => ({ ...f, packages }));
                                                }} className="h-8" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <Button size="sm" variant="outline" className="mt-2" onClick={() => setPricingForm(f => ({ ...f, packages: [...f.packages, { label: 'Nuevo', credits: 10, price_mxn: 199 }] }))}>
                                    <PlusCircle className="h-3 w-3 mr-1" />Añadir paquete
                                </Button>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" onClick={() => setPricingOpen(false)}>Cancelar</Button>
                            <Button onClick={savePricing} disabled={busy}>Guardar precios</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
